/**
 * Apollo Client singleton for the PromoStork web app — browser only.
 *
 * Importing this module creates nothing (web plan W4.2): the client, its cache
 * and its links are built by the first `getApolloClient()` call, which only a
 * browser may make. The server never creates or uses an Apollo client; it can
 * still import the module (type policies, cache key, retry rules).
 *
 * - HttpLink → GRAPHQL_URL (backend, port 4000).
 * - authLink reads the persisted session from localStorage (`@AuthData`,
 *   JSON `{ user, token }` — same shape as mobile) and sets the Bearer header.
 * - errorLink: a GraphQL error with message 'Authentication Must Be Provided'
 *   clears the stale session (token + persisted cache) and triggers a
 *   debounced (3s) hard redirect to /login, so the next mount is a clean
 *   guest session instead of a redirect loop.
 * - retryLink (below errorLink, so errorLink sees only the final outcome):
 *   a QUERY answered with the rate limiter's HTTP 429 is retried after the
 *   body's `retryAfter` when that is short. Mutations are never retried.
 * - InMemoryCache type policies (`cacheTypePolicies`): the published* list
 *   fields keep one entry per filter, page and country (pages own their
 *   pagination), the two nearby feeds append pages into one entry per point,
 *   radius, filter and country, and the entity fields the request's country
 *   decides (`urlPath`, `seo`, the stores and promotions lists) keep one
 *   value per country.
 * - Cache persisted to localStorage via apollo3-cache-persist (5 MB cap),
 *   under a versioned key: a persisted cache from another APOLLO_CACHE_VERSION
 *   is deleted, never restored.
 *
 * main.tsx renders with `getApolloClient()` straight away and calls
 * `restorePersistedCache()` in the first commit's effects, so the first
 * render (and the page chunk's request) no longer waits for the persisted
 * cache to be read and parsed (see there).
 * Non-React call sites (services/tracking.ts) use `getApolloClient()` too.
 */
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  from,
  type ApolloLink,
  type NormalizedCacheObject,
  type Operation,
  type TypePolicies,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import { CachePersistor, LocalStorageWrapper } from 'apollo3-cache-persist';
import { AUTH_STORAGE_KEY, GRAPHQL_URL } from '@/constants/config';
import { DEFAULT_LOCALE } from '@/seo/locales';
import { LOCALE_ROUTES_LIVE } from '@/seo/urls';
import { httpStatus, retryAfterSeconds } from '@/utils/validation/authErrors';

// ─── Links ──────────────────────────────────────────────────────────────────
// Built by getApolloClient(), never at import.

function readToken(): string {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return '';
    const token = (JSON.parse(raw) as { token?: string }).token;
    return token ?? '';
  } catch {
    return '';
  }
}

const createAuthLink = (): ApolloLink =>
  setContext((_, { headers }) => {
    const token = readToken();
    return {
      headers: {
        ...(headers as Record<string, string> | undefined),
        authorization: token
          ? token.startsWith('Bearer ')
            ? token
            : `Bearer ${token}`
          : '',
      },
    };
  });

/**
 * Debounced (3s) redirect so a burst of failing authenticated queries doesn't
 * fire multiple navigations. Clears the stale session first — otherwise the
 * expired token keeps failing queries and re-triggering the redirect forever.
 * Redirect itself is skipped when already on /login.
 */
let lastAuthRedirect = 0;
function redirectToLoginOnce(): void {
  const now = Date.now();
  if (now - lastAuthRedirect < 3000) return;
  lastAuthRedirect = now;
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Storage blocked — the redirect below still applies.
  }
  void clearApolloStorage();
  if (window.location.pathname === '/login') return;
  window.setTimeout(() => {
    window.location.assign('/login');
  }, 0);
}

const createErrorLink = (): ApolloLink =>
  onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.message === 'Authentication Must Be Provided') {
          redirectToLoginOnce();
        }
        if (import.meta.env.DEV) {
          console.warn(`[GraphQL error] ${err.message}`, err.path ?? '');
        }
      }
    }
    if (networkError && import.meta.env.DEV) {
      console.warn('[Network error]', networkError);
    }
  });

/**
 * Wait out a short rate-limit window instead of failing the query.
 *
 * The public mount's limiter answers a burst with HTTP 429 and a GraphQL-shaped
 * body whose `extensions.retryAfter` is the seconds left in its window (the
 * Retry-After header is not CORS-exposed, so the body is where it is read).
 * Without this a list page that hits it mid-scroll shows "Couldn't load more",
 * and its Retry fails again straight away.
 *
 *  - QUERIES only. A mutation is never replayed here: view tracking has its
 *    own queue and backoff (services/tracking.ts), and nothing else may run
 *    twice.
 *  - HTTP 429 only; every other failure surfaces exactly as before.
 *  - Only when `retryAfter` is known and at most RATE_LIMIT_MAX_WAIT_S — a
 *    longer wait is better shown to the user than hidden behind a spinner.
 *  - At most RATE_LIMIT_MAX_RETRIES retries, each after `retryAfter` plus a
 *    little jitter so tabs sharing one IP don't all come back at once.
 */
export const RATE_LIMIT_MAX_RETRIES = 2;
export const RATE_LIMIT_MAX_WAIT_S = 10;
const RATE_LIMIT_JITTER_MS = 250;

function isQuery(operation: Pick<Operation, 'query'>): boolean {
  const definition = getMainDefinition(operation.query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'query';
}

/** RetryLink `attempts`: `count` is 1 on the first failure. */
export function shouldRetryRateLimited(
  count: number,
  operation: Pick<Operation, 'query'>,
  error: unknown,
): boolean {
  if (count > RATE_LIMIT_MAX_RETRIES || !isQuery(operation)) return false;
  if (httpStatus(error) !== 429) return false;
  const wait = retryAfterSeconds(error);
  return wait !== undefined && wait <= RATE_LIMIT_MAX_WAIT_S;
}

/** RetryLink `delay`: the 429's `retryAfter`, plus jitter. */
export function rateLimitRetryDelay(
  _count: number,
  _operation: Pick<Operation, 'query'>,
  error: unknown,
): number {
  const wait = retryAfterSeconds(error) ?? 1;
  return wait * 1000 + Math.floor(Math.random() * RATE_LIMIT_JITTER_MS);
}

const createRetryLink = (): ApolloLink =>
  new RetryLink({
    attempts: shouldRetryRateLimited,
    delay: rateLimitRetryDelay,
  });

// ─── Cache ──────────────────────────────────────────────────────────────────

/** Replace the whole cached value on every fetch — pages own pagination. */
/**
 * Accumulating page policy for the two geo feeds.
 *
 * `nearbyStores`/`nearbyShops` used to be fetched as one 500-row request
 * because the Nearby page needs the whole in-radius set (it narrows by the
 * radius pills client-side and shows an area-overview strip). That only worked
 * because the backend had no cap on `limit`. It now pulls the same set in
 * bounded pages, which needs page N to append to page N-1 instead of replacing
 * it — hence `page` being excluded from `keyArgs` and the de-duplicating merge.
 */
const nearbyAppendPolicy = (itemsKey: 'stores' | 'shops') => ({
  // A nested array names the fields of the key right before it, so 'options'
  // must be its own entry. It used to be `'radiusKm', ['options', [...]]`,
  // which read `radiusKm.options.*` and keyed the entry on lat/lng alone: every
  // radius (and filter) shared one entry.
  //
  // `country` (web plan W0.19): the backend answers per request country, so
  // two countries must never share an entry. An absent key is left out of the
  // cache key; nothing sends one before W2.1.
  keyArgs: ['lat', 'lng', 'radiusKm', 'options', ['where', 'sort', 'published', 'country']] as const,
  merge(
    existing: Record<string, unknown> | undefined,
    incoming: Record<string, unknown>,
    { args }: { args: Record<string, unknown> | null },
  ) {
    const page = Number(
      (args?.['options'] as { paginationOptions?: { page?: number } } | undefined)?.paginationOptions?.page ?? 1,
    );
    const incomingItems = (incoming?.[itemsKey] as unknown[]) ?? [];
    // Page 1 is a fresh load (new fix, new radius, refetch) — start over.
    if (!existing || page <= 1) return incoming;

    const existingItems = (existing[itemsKey] as unknown[]) ?? [];
    const seen = new Set(existingItems.map((r) => (r as { __ref?: string })?.__ref));
    const merged = [...existingItems, ...incomingItems.filter((r) => !seen.has((r as { __ref?: string })?.__ref))];
    return { ...incoming, [itemsKey]: merged };
  },
});

/**
 * One cache entry per (filter, page) for the paginated catalog feeds.
 *
 * This used to be `keyArgs: false` with a replace-on-merge, which put EVERY
 * page of EVERY filter in a single entry. With `cache-and-network` and a cache
 * persisted to localStorage, opening /brands at page 1 then read back whatever
 * page had been fetched last — commonly the final page from an earlier visit.
 * The list rendered that page's `pagination`, concluded there was nothing more
 * to load, and stopped: paging worked or didn't depending on what happened to
 * be cached, which is what made "load more" intermittent.
 *
 * Naming the page (and the filter args) in `keyArgs` keeps pages apart, so a
 * read always answers with the page that was asked for. The catalog pages
 * accumulate the pages they have fetched themselves. `country` keeps each
 * request country's lists apart (see the nearby policy above).
 */
const listPagePolicy = {
  keyArgs: ['options', ['where', 'sort', 'published', 'paginationOptions', 'country']] as const,
};

/**
 * The country a request was made for (backend B0.9): the detail queries'
 * `country`, a list's `options.country`, a page query's `locale` (`bh-en`);
 * the default country (the backend's) when a request names none.
 */
export function countryOfVariables(variables: Record<string, unknown> | null | undefined): string {
  const options = variables?.['options'] as { country?: unknown; locale?: unknown } | null | undefined;
  const direct = variables?.['country'] ?? options?.country;
  if (typeof direct === 'string' && direct.trim()) return direct.trim().toUpperCase();
  const locale = variables?.['locale'] ?? options?.locale;
  const prefix = typeof locale === 'string' ? /^([a-z]{2})-[a-z]{2}$/i.exec(locale.trim())?.[1] : undefined;
  return (prefix ?? DEFAULT_LOCALE.cc).toUpperCase();
}

/** Marks a stored value as a per-country bucket (see `countryScopedField`). */
const COUNTRY_BUCKETS = '__countryBuckets';

type Buckets = Record<string, unknown> & { [COUNTRY_BUCKETS]: true };

const isBuckets = (value: unknown): value is Buckets =>
  Boolean(value) && typeof value === 'object' && (value as Record<string, unknown>)[COUNTRY_BUCKETS] === true;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value) && !('__ref' in (value as object));

/**
 * A field of an entity whose value depends on the request's country: the
 * entity is one normalised object whichever country asked (`Brand:{id}`), but
 * its `urlPath` (`/bh/en/brand/x`), its `seo`, its stores and promotions are
 * that country's. One value per country, read back for the country of the
 * query that asks, so opening `/bh/en/brand/x` after `/pk/en/brand/x` (or the
 * same brand's card on the other country's list) never shows, links to or
 * redirects with the other country's copy. A value written before this policy
 * (not a bucket) reads as missing and is fetched again.
 *
 * `replace`: the new value replaces the country's old one (lists of
 * references); otherwise object values merge, so a later query selecting
 * other fields of `seo` adds to the first one's.
 */
function countryScopedField({ replace = false }: { replace?: boolean } = {}) {
  return {
    merge(existing: unknown, incoming: unknown, { variables }: { variables?: Record<string, unknown> | undefined }) {
      const country = countryOfVariables(variables);
      const buckets: Record<string, unknown> = isBuckets(existing) ? { ...existing } : {};
      const before = buckets[country];
      buckets[country] =
        !replace && isPlainObject(before) && isPlainObject(incoming) ? { ...before, ...incoming } : incoming;
      return { ...buckets, [COUNTRY_BUCKETS]: true };
    },
    read(existing: unknown, { variables }: { variables?: Record<string, unknown> | undefined }) {
      return isBuckets(existing) ? existing[countryOfVariables(variables)] : undefined;
    },
  };
}

/** The entity fields the request's country decides (see `countryScopedField`). */
const entityCountryFields = (...lists: string[]) => ({
  urlPath: countryScopedField(),
  seo: countryScopedField(),
  ...Object.fromEntries(lists.map((name) => [name, countryScopedField({ replace: true })])),
});

/** Exported so tests can build a cache that pages exactly like the app's. */
export const cacheTypePolicies: TypePolicies = {
  Query: {
    fields: {
      publishedBrands: listPagePolicy,
      publishedStores: listPagePolicy,
      // Mobile omitted publishedShops, but it follows the exact same shape —
      // included here so the shops catalog behaves identically.
      publishedShops: listPagePolicy,
      publishedPromotions: listPagePolicy,
      // Keyed by the query point + radius so a new geolocation fix caches
      // separately. Pagination is deliberately NOT part of the key: the page
      // walks successive pages and accumulates them (see `useNearbyList`), so
      // page 2 must merge into page 1 rather than land in its own cache entry.
      nearbyStores: nearbyAppendPolicy('stores'),
      nearbyShops: nearbyAppendPolicy('shops'),
    },
  },
  Brand: {
    fields: entityCountryFields('promotions', 'brandStores', 'cityCounts', 'brandStoresCount'),
  },
  Store: {
    fields: entityCountryFields('promotions'),
  },
  Shop: {
    fields: entityCountryFields('promotions'),
  },
  Promotion: {
    fields: entityCountryFields('stores', 'shops', 'saleEvents'),
  },
  // The URL contract (backend B1.4, web plan W0.16).
  //
  // A resolution carries the entity's `id`, so by default it would be
  // normalised as `EntityPathResolution:{id}` — and every key of one entity
  // (its slug: 200, an old slug or the id under a prefix: 301) would share, and
  // overwrite, one object: a page could then read another key's 301 to its
  // own path and redirect in a loop. Kept inline under each
  // `resolveEntityPath(type, key, locale)` instead.
  EntityPathResolution: { keyFields: false },
  // No id: stored inside its entity. Merged, so a query selecting other `seo`
  // fields later adds to the detail query's `robots` instead of replacing it.
  EntitySeo: { merge: true },
};

// ─── Client ─────────────────────────────────────────────────────────────────

let client: ApolloClient<NormalizedCacheObject> | null = null;

/**
 * The browser's one Apollo client, created on the first call (web plan W4.2).
 * Throws without a `window`: the server renders without Apollo, so a call
 * there is a bug (a component reaching for the client during render instead
 * of in an effect or handler), not something to paper over.
 */
export function getApolloClient(): ApolloClient<NormalizedCacheObject> {
  if (typeof window === 'undefined') {
    throw new Error('getApolloClient() is browser-only: the server never creates an Apollo client.');
  }
  client ??= new ApolloClient({
    link: from([createAuthLink(), createErrorLink(), createRetryLink(), new HttpLink({ uri: GRAPHQL_URL })]),
    cache: new InMemoryCache({ typePolicies: cacheTypePolicies }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
  return client;
}

// ─── Persistence ────────────────────────────────────────────────────────────

/**
 * Version of the cache persisted to localStorage. Bump it whenever a cached
 * copy would show what the API no longer serves — above all when the public
 * visibility rules change. Every query is cache-and-network, so a returning
 * visitor paints the persisted cache first: without a bump they would see the
 * blocked brands, drafts and stores abroad it still holds until the refetch
 * lands (web plan W0.19).
 *
 * The version is part of the storage key, and `restorePersistedCache()`
 * deletes every persisted cache under another key before it restores, so an
 * old cache is never read, not even for the page's first render. A separate
 * "version" entry would not be enough: a tab still running the previous build
 * keeps writing its cache, and it would land under the new version's marker.
 *
 *  1  the unversioned `apollo-cache-persist` key (before W0.19)
 *  2  backend B0.9: blocked entities and drafts hidden on every read path
 *  3  web W0.16: the detail fragments select `urlPath`, `seo { robots }` and
 *     `phase`, and `resolveEntityPath` answers are stored inline (not by id)
 *  4  web WF (backend B3.4 / B3.5): backend B3.5 hides a promotion without an
 *     end date 21 days after it was last verified (every public list), and
 *     the fragments select the staff content and the structured offer
 *     (`headline`, discount, terms, `seoOverrides`, opening hours, …); ship
 *     it in the web release that follows the B3.5 backend release
 *
 * NEXT BUMP: in the web release that follows backend BG2 being applied on
 * production (entities outside Pakistan hidden). Bumping only for B0.9 would
 * let caches refill with the stores abroad, so it has to be bumped again then.
 */
export const APOLLO_CACHE_VERSION = 4;

/** apollo3-cache-persist's default key, used before the cache was versioned. */
const LEGACY_CACHE_KEY = 'apollo-cache-persist';

/**
 * Where this version's cache lives; other versions' keys are deleted. A build
 * with the URL move live (`LOCALE_ROUTES_LIVE`, web plan W2) keeps its cache
 * under its own key, so the flip starts every returning visitor on an empty
 * cache (no persisted pre-move resolution sends a page back to its old id path
 * for a first paint), and a roll-back does the same the other way. Unchanged
 * while the switch is off.
 */
export const APOLLO_CACHE_KEY = `${LEGACY_CACHE_KEY}:v${APOLLO_CACHE_VERSION}${LOCALE_ROUTES_LIVE ? ':url-move' : ''}`;

const isPersistedCacheKey = (key: string): boolean =>
  key === LEGACY_CACHE_KEY || key.startsWith(`${LEGACY_CACHE_KEY}:`);

/** Delete every persisted cache written under another version's key. */
function dropStalePersistedCaches(storage: Storage): void {
  const stale: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key !== null && key !== APOLLO_CACHE_KEY && isPersistedCacheKey(key)) stale.push(key);
  }
  for (const key of stale) storage.removeItem(key);
}

/**
 * A persisted snapshot with what the cache already holds laid over it, entity
 * by entity and field by field: whatever the network (or the app) wrote since
 * the page loaded is newer than the copy on disk, so it wins. Exported for
 * tests.
 */
export function mergeCacheSnapshots(
  persisted: NormalizedCacheObject,
  current: NormalizedCacheObject,
): NormalizedCacheObject {
  const merged: NormalizedCacheObject = { ...persisted };
  for (const [id, fields] of Object.entries(current)) {
    const older = merged[id];
    merged[id] =
      older && typeof older === 'object' && fields && typeof fields === 'object'
        ? { ...older, ...fields }
        : fields;
  }
  return merged;
}

let persistor: CachePersistor<NormalizedCacheObject> | null = null;
let restoreStarted = false;

/**
 * Restore the persisted cache into the client's cache and start persisting
 * its writes — once per page load; later calls do nothing.
 *
 * main.tsx calls it in the first commit's effects, which it flushes with the
 * commit itself (`flushSync`): after the first render (reading and parsing up
 * to 5 MB of JSON no longer holds it back, nor the request for the page's
 * lazy chunk, which that render starts) and before the browser paints that
 * commit, so the first frame React paints, and the lazy page's first render,
 * already show the restored cache on a repeat visit.
 *
 *  1. Stale versions' caches are deleted first (W0.19), before anything reads.
 *  2. The current version's copy is read and parsed synchronously, and merged
 *     UNDER what the cache holds already (`mergeCacheSnapshots`): a response
 *     that arrived first is never replaced by the older copy. The cache's
 *     watchers are then told, so the queries the first render already read
 *     (empty) read the restored data.
 *  3. apollo3-cache-persist then writes the cache back on every change.
 *
 * Failures degrade gracefully: with storage blocked or broken the app runs on
 * an empty cache instead of not starting at all.
 */
export function restorePersistedCache(): void {
  if (restoreStarted) return;
  restoreStarted = true;
  const { cache } = getApolloClient();
  let storage: Storage;
  try {
    // Reading `window.localStorage` itself throws where site data is blocked.
    storage = window.localStorage;
    dropStalePersistedCaches(storage);
    persistor = new CachePersistor({
      cache,
      storage: new LocalStorageWrapper(storage),
      key: APOLLO_CACHE_KEY,
      maxSize: 1048576 * 5, // 5 MB
      debug: import.meta.env.DEV,
    });
  } catch (error) {
    console.warn('Cache persist initialization failed:', error);
    return;
  }
  try {
    const persisted = storage.getItem(APOLLO_CACHE_KEY);
    if (persisted) {
      cache.restore(
        mergeCacheSnapshots(JSON.parse(persisted) as NormalizedCacheObject, cache.extract()),
      );
      // `restore()` tells no one. A query rendered before it (the header's
      // shops count, in the first render) keeps the empty-cache read it made
      // then, and when it subscribes, cache-and-network answers from that
      // stale read and waits for the network: the header's Shops link would
      // appear a round trip later and shift the desktop nav. An empty batch
      // is the public way to broadcast to the cache's watchers, so they read
      // the restored data first. (Not `restore` inside the batch: `batch`
      // puts the previous data back after its update.)
      cache.batch({ update: () => {} });
    }
  } catch (error) {
    // Unreadable or corrupt: start empty; the next write replaces it.
    console.warn('Cache persist initialization failed:', error);
  }
}

/**
 * Wipe the in-memory Apollo cache AND its localStorage persistence — the
 * pattern documented by apollo3-cache-persist for user logout. Called on
 * sign-out and on stale-token auth errors so one user's cached data
 * (notifications, favorites, isFavorite flags) never leaks into the next
 * session or lingers on disk.
 */
export async function clearApolloStorage(): Promise<void> {
  try {
    await client?.clearStore();
  } finally {
    if (persistor) {
      await persistor.purge();
    } else {
      // Before the restore ran: the copy on disk is the old session's, so the
      // restore must find nothing.
      try {
        window.localStorage.removeItem(APOLLO_CACHE_KEY);
      } catch {
        // Storage blocked (or no window) — nothing was persisted either.
      }
    }
  }
}
